import { ROW_NUMBER_COLUMN_ID } from '@/Utils/gridConstants';
import { flushSync } from 'react-dom';
import { getFileUuidFromPath } from '@/Utils/getFileUuidFromPath';
import { restoreGridState, setIsInsertingNewColumn } from '@/redux/reducers/spreadsheetSlice';
import { get, put } from '@/Utils/API';
import { TOAST_TYPE_ERROR, URL_DATASET } from '@/Utils/constants';
import { fetchOpenedFile } from '@/redux/reducers/datasetSlice';
import {
  getCellRangeForPaste,
  getClipboardWidth,
} from '@/pages/SpreadsheetPage/PasteUtils/PasteHelper';
import ShowToast from '@/Components/Toast/showToastTemplate';

export const getPasteStartPosition = (gridRef) => {
  try {
    const focusedCell = gridRef.current.api.getFocusedCell();
    const { colId } = focusedCell.column;
    const { rowIndex } = focusedCell;
    const rowNumber =
      gridRef.current.api.getDisplayedRowAtIndex(rowIndex).data[ROW_NUMBER_COLUMN_ID];

    if (isNaN(rowNumber) || rowNumber < 1) {
      throw new Error('Not a valid row number: ' + rowNumber);
    }

    if (
      colId === ROW_NUMBER_COLUMN_ID ||
      !gridRef.current.api.getColumns().some((column) => column.colId === colId)
    ) {
      throw new Error('Not a valid column id: ' + colId);
    }

    return { col: colId, row: rowNumber, rowIndex: rowIndex };
  } catch (error) {
    console.error(error);
    throw new Error('Failed to get paste start position');
  }
};

export const selectPasteArea = (gridRef, startPosition, clipboardData) => {
  try {
    gridRef?.current?.api.deselectAll();
    gridRef?.current?.api.clearCellSelection();
    gridRef?.current?.api.addCellRange(
      getCellRangeForPaste(startPosition, clipboardData, gridRef?.current?.api.getColumns())
    );
  } catch (error) {
    console.error(error);
  }
};

export const _processDataFromClipboard = (params, clientState, gridRef, dispatch) => {
  const pasteAllowedStatus = checkPasteAllowed(clientState);

  if (pasteAllowedStatus === PASTE_ALLOWED) {
    let clipboardData = params.data;
    const requestAgGrid = gridRef.current.api.getGridOption('serverSideDatasource')?.state;
    const startPosition = getPasteStartPosition(gridRef);

    const isGrouped = clientState?.columnState?.some((e) => e.rowGroup);
    if (isGrouped) {
      const startNode = getStartNode(gridRef, startPosition);
      requestAgGrid['groupKeys'] = getGroupKeys(startNode);
      clipboardData = truncateClipboardDataForGroupedSheet(gridRef, clipboardData, startNode);
    }

    selectPasteArea(gridRef, startPosition, clipboardData);
    const _isPastedInSortedColumn = isPastedInSortedColumn(
      gridRef,
      startPosition,
      clipboardData,
      requestAgGrid
    );
    flushSync(() => {
      const undoData = {
        startPosition: startPosition,
        clipboardData: getPrevClipboardData(gridRef),
        requestAgGrid: requestAgGrid,
      };
      const undoPossible =
        !isGrouped && isUndoPossible(gridRef, clipboardData, _isPastedInSortedColumn);
      execPaste(dispatch, startPosition, clipboardData, requestAgGrid, undoData, undoPossible);
    });
  } else {
    showToast({ type: 'error', text: pasteAllowedStatus });
  }
};

const execPaste = (
  dispatch,
  _startPosition,
  _clipboardData,
  _requestAgGrid,
  undoData,
  undoPossible,
  isUndoAction = false
) => {
  const fileAndFunction = '__FILE_AND_FUNCTION_NAME__';
  const toastErrorText = 'Paste failed. Please try again later.';
  let endpoint = '';
  let payload = {};
  let responseStatus = -1;

  const showErrorToast = (errorContext) => {
    ShowToast({
      type: TOAST_TYPE_ERROR,
      text: toastErrorText,
      errorContext: errorContext,
      fileAndFunction: fileAndFunction,
      endpoint: endpoint,
      payload: payload,
      responseStatus: responseStatus,
    });
  };

  try {
    const uuid = getFileUuidFromPath();

    if (_startPosition && _startPosition.row && _startPosition.col && uuid) {
      dispatch(setIsInsertingNewColumn(true));

      endpoint = `${URL_DATASET}/${uuid}/${_startPosition.col}/${_startPosition.row}/upsert-rows`;
      payload = {
        cells: _clipboardData,
        requestAgGrid: _requestAgGrid,
      };

      put(endpoint, payload)
        .then((response) => {
          responseStatus = response?.status;
          if (!response.Success) {
            dispatch(setIsInsertingNewColumn(false));
            showErrorToast(response);
          } else {
            get(`${URL_DATASET}/${uuid}`, true).then((currentFileResponse) => {
              currentFileResponse.json().then((currentFile) => {
                dispatch(setIsInsertingNewColumn(false));
                dispatch(fetchOpenedFile(uuid));
                dispatch(restoreGridState(currentFile?.ClientState));
                if (undoPossible && !isUndoAction) {
                  showUndoToast(dispatch, undoData);
                }
              });
            });
          }
        })
        .catch((error) => {
          dispatch(setIsInsertingNewColumn(false));
          showErrorToast(error);
        });
    } else {
      dispatch(setIsInsertingNewColumn(false));
      showErrorToast();
    }
  } catch (error) {
    dispatch(setIsInsertingNewColumn(false));
    showErrorToast(error);
  }
};

const showUndoToast = (dispatch, undoData) => {
  const toastBody = ({ closeToast }) => (
    <div>
      <p>Paste successful!</p>
      {/* eslint-disable-next-line */}
      <p
        id='undo-paste-btn'
        className='hover:cursor-pointer text-ocean-blue-600 hover:underline'
        onClick={() => {
          closeToast();
          undoPaste(dispatch, undoData);
        }}
      >
        Undo?
      </p>
    </div>
  );

  showToast({ type: 'info', customBody: toastBody });
};

const undoPaste = (dispatch, _pasteUndoData) => {
  if (_pasteUndoData) {
    execPaste(
      dispatch,
      _pasteUndoData.startPosition,
      _pasteUndoData.clipboardData,
      _pasteUndoData.requestAgGrid,
      null,
      true,
      true
    );
  }
};

const isUndoPossible = (gridRef, clipboardData, _isPastedInSortedColumn) => {
  try {
    if (_isPastedInSortedColumn) {
      return false;
    }
    const cellRange = gridRef?.current?.api.getCellRanges()[0];
    const endRow = gridRef?.current?.api.getDisplayedRowAtIndex(cellRange.endRow.rowIndex);
    const rowOutOfBounds = !endRow.hasOwnProperty('data');
    const columnOutOfBounds = getClipboardWidth(clipboardData) > cellRange.columns.length;
    return !rowOutOfBounds && !columnOutOfBounds;
  } catch (e) {
    return false;
  }
};

const getStartNode = (gridRef, startPosition) => {
  let startNode = null;
  gridRef.current.api.forEachNode((node) => {
    if (node.rowIndex === startPosition.rowIndex) {
      startNode = node;
    }
  });
  return startNode;
};

const getGroupKeys = (startNode) => {
  const groupKeys = [];
  let node = startNode;
  while (node && node.parent?.key !== null) {
    groupKeys.push(node.parent.key);
    node = node.parent;
  }
  return groupKeys.reverse();
};

// truncate clipboard data so that it fits the number of rows in the expanded group node
const truncateClipboardDataForGroupedSheet = (gridRef, clipboardData, startNode) => {
  let startCounting = false;
  let count = 0;
  gridRef.current.api.forEachNode((node) => {
    if (node.rowIndex === startNode.rowIndex) {
      startCounting = true;
    } else if (node.group || count >= clipboardData.length) {
      startCounting = false;
    }
    if (startCounting) {
      count = count + 1;
    }
  });

  return clipboardData.slice(0, count);
};

const isPastedInSortedColumn = (gridRef, startPosition, clipboardData, requestAgGrid) => {
  try {
    const columns = gridRef?.current?.api.getColumns();
    const columnIds = columns
      .slice(columns.findIndex((c) => c.colId === startPosition.col))
      .filter((c) => c.visible)
      .slice(0, clipboardData[0].length)
      .map((c) => c.colId);

    if (requestAgGrid.sortModel.some((e) => columnIds.includes(e.colId))) {
      return true;
    }
  } catch (error) {
    console.error(error);
  }

  return false;
};

const getPrevClipboardData = (gridRef) => {
  const prevClipboardData = [];
  try {
    const cellRange = gridRef?.current?.api.getCellRanges()[0];
    for (let { rowIndex } = cellRange.startRow; rowIndex <= cellRange.endRow.rowIndex; rowIndex++) {
      prevClipboardData.push(
        cellRange.columns
          .map((c) => c.colId)
          .map((colId) => gridRef?.current?.api.getDisplayedRowAtIndex(rowIndex)?.data[colId])
          .map((v) => `${v}`)
      );
    }
  } catch (e) {
    console.error(e);
  }

  return prevClipboardData;
};

export const PASTE_ALLOWED = 'paste_allowed';
export const checkPasteAllowed = () => {
  try {
    return PASTE_ALLOWED;
  } catch (error) {
    return 'Paste is not allowed';
  }
};
